/** Analyst Agent shadow v0 운영 관찰 패널. */
import { useEffect, useState } from 'react';
import {
  AnalystShadowSummary,
  fetchAnalystShadowSummary,
  runAnalystShadow,
} from '../../services/operationsApi';

interface Props {
  summary?: AnalystShadowSummary;
  onUpdated: () => void;
}

export default function OperationsAnalystShadow({ summary, onUpdated }: Props) {
  const [hours, setHours] = useState(24);
  const [view, setView] = useState<AnalystShadowSummary | undefined>(summary);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (hours === 24) {
      setView(summary);
    }
  }, [hours, summary]);

  const changeHours = async (nextHours: number) => {
    setHours(nextHours);
    setMessage(null);
    if (nextHours === 24) {
      setView(summary);
      return;
    }
    setRunning(true);
    try {
      setView(await fetchAnalystShadowSummary(nextHours));
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Analyst shadow 조회 실패');
    } finally {
      setRunning(false);
    }
  };

  const run = async () => {
    setRunning(true);
    setMessage(null);
    try {
      const result = await runAnalystShadow(5, hours);
      setView(result);
      setMessage(result.status === 'saved' ? `${result.saved_count || 0}건 저장` : statusLabel(result.status));
      onUpdated();
    } catch (err) {
      setMessage(err instanceof Error ? err.message : 'Analyst shadow 실행 실패');
    } finally {
      setRunning(false);
    }
  };

  if (!view) {
    return (
      <section className="op-analyst-panel">
        <h3>Analyst Shadow</h3>
        <p className="op-empty">Analyst shadow 데이터 없음</p>
      </section>
    );
  }

  return (
    <section className="op-analyst-panel">
      <div className="op-analyst-head">
        <div>
          <h3>Analyst Shadow</h3>
          <p>Gemini 분석을 별도 DecisionRecord로 저장 · 주문 영향 없음 · {view.version}</p>
        </div>
        <div className="op-analyst-controls">
          <select value={hours} onChange={(event) => changeHours(Number(event.target.value))} disabled={running}>
            <option value={24}>최근 24시간</option>
            <option value={168}>최근 7일</option>
            <option value={720}>최근 30일</option>
          </select>
          <button className="op-refresh" onClick={run} disabled={running || view.pending_count === 0}>
            {running ? '처리 중...' : 'Shadow 실행'}
          </button>
        </div>
      </div>
      {view.pending_count === 0 && (
        <div className="op-analyst-message">
          선택 기간에 분석할 신규 rule decision 없음. 기간을 넓히거나 장중 decision 발생 후 실행.
        </div>
      )}
      {message && <div className="op-analyst-message">{message}</div>}
      <div className="op-analyst-metrics">
        <Metric label="Rule" value={view.rule_decision_count} />
        <Metric label="Shadow" value={view.shadow_decision_count} />
        <Metric label="Pending" value={view.pending_count} tone={view.pending_count > 0 ? 'warn' : 'ok'} />
        <Metric label="Agree" value={view.agreement.agree} />
        <Metric label="Disagree" value={view.agreement.disagree} tone={view.agreement.disagree > 0 ? 'warn' : undefined} />
      </div>
      {view.auto && (
        <div className="op-analyst-auto">
          <span>자동 {view.auto.enabled ? 'ON' : 'OFF'}</span>
          <span>오늘 {view.auto.today_processed_count}/{view.auto.daily_row_limit}</span>
          <span>남은 처리 {view.auto.today_remaining}</span>
          <span>배치 {view.auto.batch_size}</span>
        </div>
      )}
      {view.analysis?.action_matrix?.length ? (
        <div className="op-analyst-list">
          {view.analysis.action_matrix.slice(0, 8).map((item) => (
            <span key={`${item.rule_action}-${item.analyst_action}`}>
              {item.rule_action} {'→'} {item.analyst_action} <strong>{item.count}</strong>
            </span>
          ))}
        </div>
      ) : null}
      <div className="op-analyst-list">
        {view.latest.map((item) => (
          <span key={item.id}>
            <strong>{item.symbol}</strong> {item.rule_action || '-'} {'→'} {item.action}
            {item.agreement ? ` · ${item.agreement}` : ''}
          </span>
        ))}
        {view.latest.length === 0 && <p className="op-empty">아직 shadow 판단 없음</p>}
      </div>
    </section>
  );
}

function Metric({ label, value, tone }: { label: string; value: number; tone?: 'ok' | 'warn' }) {
  return (
    <span className={tone ? `tone-${tone}` : undefined}>
      <small>{label}</small>
      <strong>{value}</strong>
    </span>
  );
}

function statusLabel(status?: string) {
  if (status === 'no_candidates') return '분석할 신규 decision 없음';
  if (status === 'daily_cap_reached') return '오늘 자동/수동 처리 한도 도달';
  if (status === 'error') return '실행 오류';
  return '실행 완료';
}
